import type { FC } from 'react'
import { memo } from 'react'
import styled from '@emotion/styled'

import ColumnTrack from '@segment/matcha/components/ColumnTrack'
import ButtonText from '@segment/matcha/components/ButtonText'
import { ChevronRightIcon } from '@segment/matcha/components/Icons/Common'

import CardBase, * as CardRecipe from '@segment/matcha/recipes/Card'
import CopyTextBase, * as CopyText from '@segment/matcha/recipes/CopyText'
import SectionBase, * as Section from '@segment/matcha/recipes/Section'

import getAbsoluteUrl from '@segment/utils/getAbsoluteUrl'

import { usePageConfig } from '@segment/contexts/page'

type Resource = {
  slug: string
  title: string
  description: string
  cta: string
}

const copy = {
  heading: `Additional Resources`,
}

const data: Resource[] = [
  {
    slug: `/growth-center/`,
    title: `Growth Center`,
    description: `Learn how to implement and scale high-performing growth strategies.`,
    cta: `Visit our Growth Center`,
  },
  {
    slug: `https://segment.com/blog/`,
    title: `Our Blog`,
    description: `Stay up-to-date with our latest articles on all things data.`,
    cta: `Read our blog`,
  },
  {
    slug: `/good-data-better-marketing/`,
    title: `Good Data, Better Marketing`,
    description: `Tune in to hear from enterprise experts as they share how to use data as a competitive asset to drive customer engagement.`,
    cta: `Listen to our podcast`,
  },
]

const AdditionalResources: FC = memo(() => {
  const pageConfig = usePageConfig()

  return (
    <StyledSection variant="centered">
      <Section.Copy>
        <CopyTextBase centered={true}>
          <CopyText.Heading tag="h2" variant="h2">
            {copy.heading}
          </CopyText.Heading>
        </CopyTextBase>
      </Section.Copy>
      <Section.Contents>
        <ColumnTrack
          columns={1}
          overrides={{
            lg: 3,
          }}
        >
          {data.map(({ slug, title, description, cta }) => (
            <CardBase key={slug} href={getAbsoluteUrl(`${slug}`, pageConfig)}>
              <CardRecipe.Body>
                <CardRecipe.Copy>
                  <CardRecipe.Headline tag="h3">{title}</CardRecipe.Headline>
                  <CardRecipe.Description>{description}</CardRecipe.Description>
                </CardRecipe.Copy>
                <CardRecipe.Action alignment="left">
                  <ButtonText iconEnd={ChevronRightIcon}>{cta}</ButtonText>
                </CardRecipe.Action>
              </CardRecipe.Body>
            </CardBase>
          ))}
        </ColumnTrack>
      </Section.Contents>
    </StyledSection>
  )
})

const StyledSection = styled(SectionBase)`
  padding-bottom: 40px;

  ${Section.Wrapper} {
    row-gap: 50px;
  }

  ${({ theme }) => theme.breakpoints.up(`lg`)} {
    padding-bottom: 80px;
  }
`

export default AdditionalResources
